import {
  BehaviorSubject,
  of,
  tap,
  switchMap,
  filter,
  debounceTime,
  map,
  mergeMap,
  combineLatest,
  catchError,
} from "rxjs";
import apiClient from "./apiClient";
import store from "../state/store";
import Comment from "../models/Comment";
import Topic from "../models/Topic";

const PUB = "publications";
const CLS = "classifications";

const threadService = (() => {
  const commentStatsPipe$ = new BehaviorSubject(null);
  const buffer = [];

  function dispatchTopics(type, typeId, result) {
    const state = store.getState();
    const threadMap = state.threadMap || new Map();
    const flagVals = Object.entries(result.flag_ids || {});
    const pubVals = Object.entries(result.pubmed_ids || {});
    const flagItems = flagVals.map(([id, x]) => ({ ...x, id, type: CLS }));
    const pubItems = pubVals.map(([id, x]) => ({ ...x, id, type: PUB }));

    const threads = flagItems.concat(pubItems).map((x) => ({
      ...x,
      slug: [type, typeId, x.type, x.id].join("-"),
      isLoading: false,
    }));

    threads
      .map((x) => new Topic({ ...(threadMap.get(x.slug) || {}), ...x }))
      .reduce((a, i) => a.set(i.slug, i), threadMap);

    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  }

  function dispatchLoading(flags, pubs) {
    const state = store.getState();
    const threadMap = state.threadMap || new Map();

    flags
      .concat(pubs)
      .map((x) => ({ ...x, isLoading: true }))
      .map((x) => new Topic({ ...(threadMap.get(x.slug) || {}), ...x }))
      .reduce((a, i) => a.set(i.slug, i), threadMap);

    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  }

  function load({ type, typeId, flags, pubs }) {
    const state = store.getState();
    const threadMap = state.threadMap || new Map();
    const getStats = apiClient.getSocialStats;

    const flagItems = flags
      .map((id) => ({ id, slug: [type, typeId, CLS, id].join("-") }))
      .filter((x) => !threadMap.get(x.slug));

    const pubItems = pubs
      .map((id) => ({ id, slug: [type, typeId, PUB, id].join("-") }))
      .filter((x) => !threadMap.get(x.slug));

    return of(null).pipe(
      tap(() => dispatchLoading(flagItems, pubItems)),
      filter(() => type && typeId),
      filter(() => Boolean(flagItems.length || pubItems.length)),
      switchMap(() => getStats(type, typeId, pubItems, flagItems)),
      tap((x) => dispatchTopics(type, typeId, x))
    );
  }

  function toReqs(buffered) {
    const grouped = buffered.reduce((a, i) => {
      const key = `${i.type}-${i.typeId}`;
      const val = a[key] || {};

      val.pubs = val.pubs || new Set();
      val.flags = val.flags || new Set();
      val.type = i.type;
      val.typeId = i.typeId;

      if (i.subType === PUB) {
        val.pubs.add(i.subTypeId);
      } else if (i.subType === CLS) {
        val.flags.add(i.subTypeId);
      }

      a[key] = val;
      return a;
    }, {});

    const requests = Object.values(grouped)
      .map((x) => ({ ...x, flags: [...x.flags], pubs: [...x.pubs] }))
      .map((x) => load(x));

    return combineLatest(requests);
  }

  function updateStoreComments(slug, comments) {
    const state = store.getState();
    const threadMap = state.threadMap || new Map();
    const topic = threadMap.get(slug) || new Topic({ slug });
    const updated = topic.setComments(comments).setIsLoading(false).clone();

    threadMap.set(slug, updated);

    comments.forEach((x) => {
      const { topic: commentTopic } = x;
      threadMap.set(commentTopic.slug, commentTopic);
    });

    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  }

  function loadCommentStats(slug) {
    const [type, typeId, subType, subTypeId] = (slug || "").split("-");
    commentStatsPipe$.next({ type, typeId, subType, subTypeId });
  }

  function updateTopicIsLoading(topic, isLoading) {
    const state = store.getState();
    const threadMap = state.threadMap || new Map();
    threadMap.set(topic.slug, topic.setIsLoading(isLoading).clone());
    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  }

  function loadTopicAndComments(type, typeId, subType, subTypeId) {
    const slug = [type, typeId, subType, subTypeId].filter(Boolean).join("-");
    const { getComments } = apiClient;
    const state = store.getState();
    const threadMap = state.threadMap || new Map();
    const ex = threadMap.get(slug) || new Topic({});

    return !ex?.isFetched
      ? of(null).pipe(
          tap(() => updateTopicIsLoading(ex, true)),
          switchMap(() => getComments(type, typeId, subType, subTypeId)),
          map((x) => x.results),
          filter(Boolean),
          map((x) => x[subTypeId || typeId]),
          map((x) => (x ? x.post_stream : [])),
          map((x) => x.map((i) => new Comment(i, slug))),
          map((x) => x.reverse()),
          tap(() => updateTopicIsLoading(ex, false)),
          tap((x) => updateStoreComments(slug, x)),
          catchError((e) => {
            console.error(e);
            return of(null);
          })
        )
      : of(null);
  }

  commentStatsPipe$
    .pipe(
      filter(Boolean),
      tap((x) => buffer.push(x)),
      debounceTime(50),
      map(() => [...buffer]),
      tap(() => (buffer.length = 0)),
      mergeMap((x) => toReqs(x))
    )
    .subscribe();

  return {
    loadCommentStats,
    updateStoreComments,
    loadTopicAndComments,
  };
})();

export default Object.freeze(threadService);
